// The signed-in principal (API-038 `GET /v1/me`) and what it may do. Authorization is the
// server's; the SPA uses `can` only to hide what the principal could not use anyway (ADR-031).
import { createContext } from "preact";
import type { components } from "../api/schema";
import type { ApiClient, FetchLike, ScopeMe } from "@nexus/ui";
import { ApiError, CSRF_HEADER, type CsrfToken } from "@nexus/ui";

export type Me = components["schemas"]["Me"];
export type Permission = components["schemas"]["Permission"];

/** Any one of several permissions (api-contract `x-nexus-permission-any-of`). Super-admin is never
 *  listed explicitly: `can` below already grants a super-admin every permission, so it is covered
 *  without repeating it in the list. */
export interface AnyOfRequirement {
  readonly anyOf: readonly Permission[];
}

/** What a route needs: a platform permission, any one of several, the super-admin role, any session, or nothing. */
export type Requirement = Permission | AnyOfRequirement | "super-admin" | "signed-in" | "public";

/** Builds an any-of requirement, e.g. `anyOf("billing:read", "installer:run")` (NAV-011). */
export function anyOf(...permissions: readonly Permission[]): AnyOfRequirement {
  return { anyOf: permissions };
}

export interface Session {
  readonly me: Me;
  /** Super-admins hold every platform permission (ADR-031). */
  can(permission: Permission): boolean;
  allows(requirement: Requirement): boolean;
  /** Milliseconds until the session's absolute expiry (≤ 0 once past). */
  msUntilExpiry(now: Date): number;
}

export type SessionState =
  | { status: "signed-in"; session: Session }
  | { status: "signed-out" }
  | { status: "no-access"; who: string | null }
  | { status: "unreachable"; error: ApiError };

/** Who is signed in, for display: the name recorded at sign-in, else the email, else null. Never the
 *  subject — a SAML subject is a pseudonym (`saml-` and a hash) that means nothing to a person. */
export function displayName(me: Me): string | null {
  return me.name ?? me.email ?? null;
}

/** The principal as the shared screens read it (`@nexus/ui`'s `ScopeProvider`, ADR-101). */
export function scopeOf(me: Me): ScopeMe {
  return { id: me.subject, orgId: me.orgId, role: me.role, permissions: me.permissions };
}

export function sessionFrom(me: Me): Session {
  const superAdmin = me.role === "super-admin";
  const granted = new Set<Permission>(me.permissions);
  const can = (permission: Permission): boolean => superAdmin || granted.has(permission);
  return {
    me,
    can,
    allows(requirement) {
      if (requirement === "public" || requirement === "signed-in") return true;
      if (requirement === "super-admin") return superAdmin;
      if (typeof requirement === "object") return requirement.anyOf.some((permission) => can(permission));
      return can(requirement);
    },
    msUntilExpiry: (now) => Date.parse(me.sessionExpiresAt) - now.getTime(),
  };
}

/** What screens read from the app root: the session state and the one session action. */
export interface SessionServices {
  state: SessionState;
  signOut(): Promise<void>;
}

/** Provided by main.tsx; null outside the app root (a screen rendered on its own). */
export const SessionContext = createContext<SessionServices | null>(null);

/** A platform principal whose groups granted no permission can reach nothing (FLOW-002 "no access"). */
function hasAnyAccess(me: Me): boolean {
  return me.role !== "platform" || me.permissions.length > 0;
}

/** Load the principal, keeping its CSRF token in memory for the client's unsafe requests. */
export async function loadSession(client: ApiClient, csrf: CsrfToken): Promise<SessionState> {
  try {
    const me = await client.get("/v1/me");
    csrf.set(me.csrfToken);
    if (!hasAnyAccess(me)) return { status: "no-access", who: me.email ?? me.name ?? null };
    return { status: "signed-in", session: sessionFrom(me) };
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      csrf.clear();
      return { status: "signed-out" };
    }
    if (error instanceof ApiError) return { status: "unreachable", error };
    throw error;
  }
}

/** Only a same-origin, non-auth path may be a return target (the BFF re-checks, API-038). */
export function safeReturnTo(path: string | null | undefined): string {
  if (!path || !path.startsWith("/") || path.startsWith("//") || path.includes("\\")) return "/";
  if (/[\u0000-\u001f\u007f]/.test(path)) return "/";
  if (path === "/auth" || path.startsWith("/auth/") || path === "/signin" || path.startsWith("/signin?")) return "/";
  return path;
}

/** The BFF's login redirect (code + PKCE), coming back to `returnTo` afterwards. */
export function signInHref(returnTo: string | null | undefined): string {
  return `/auth/login?returnTo=${encodeURIComponent(safeReturnTo(returnTo))}`;
}

/** Revoke the server-side session; the page forgets its CSRF token whatever the answer. */
export async function signOut(fetch: FetchLike, csrf: CsrfToken): Promise<void> {
  const token = csrf.current();
  const headers: Record<string, string> = token === null ? {} : { [CSRF_HEADER]: token };
  try {
    await fetch("/auth/logout", { method: "POST", headers, credentials: "same-origin", redirect: "manual" });
  } finally {
    csrf.clear();
  }
}
